import type {
  RouteLocation,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import type { Acl, User } from "vue-simple-acl/types/acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  PATH_BUSINESS_UNITS_ID,
  PATH_BUSINESS_UNITS_ID_SETTINGS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_CONFIGURATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_CONFIGURATIONS_UID,
  PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
  PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES,
  PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES_UUID,
  PATH_BUSINESS_UNITS_ID_SETTINGS_LOGS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_AFFILIATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_DELEGATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_PROFILES,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_OPERATOR,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_DELEGATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_INFORMATION,
} from "~/modules/businessUnit/constants";
import { router } from "~/plugins/router";

const routes: RouteRecordRaw[] = [
  {
    path: PATH_BUSINESS_UNITS_ID,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_ORG),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
        params: { id: to.params.id },
      }),
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_ORG),
    },
  },
  // Note: Do not use redirect here because the tab "Affiliation" can be not displayed.
  //       It will be replaced in the component RelationsComponent.
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_RELATION),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_AFFILIATIONS,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_RELATION),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_DELEGATIONS,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_RELATION),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS,
        params: { id: to.params.id },
      }),
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_INFORMATION,
        params: { id: to.params.id, userLogin: to.params.userLogin },
      }),
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_INFORMATION,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_WRITE_USER),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_USERS_LOGIN_DELEGATIONS,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_WRITE_USER),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_PROFILES,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
        params: { id: to.params.id, profileId: to.params.profileId },
      }),
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_OPERATOR,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_LOGS,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_FEATURE),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES_UUID,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_FEATURE),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_CONFIGURATIONS,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_FEATURE),
    },
  },
  {
    path: PATH_BUSINESS_UNITS_ID_SETTINGS_CONFIGURATIONS_UID,
    component: () =>
      import("~/modules/businessUnit/views/BusinessUnitSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_FEATURE),
    },
  },
];

export default routes;
